import React from 'react';
import { Link } from 'react-router-dom';
import LandingLogo from '@/assets/icons/landing-logo.webp';

/**
 * Shared shell for the unauthenticated pages (/sign-in, /sign-up,
 * /forgot-password, /reset-password), styled to match the staty.io landing
 * site (apps/landing-ploy) so the landing → dash signup handoff keeps the
 * brand. Class strings and tokens are copied from the landing (see the
 * `@theme` ploy block in src/index.css) — landing-ploy exports nothing, so
 * duplication is deliberate.
 */

// Landing contact-form input (contact-section.tsx inputClass).
export const authInput =
  'w-full rounded-lg border border-ploy-input-border bg-ploy-input-background px-4 py-3.5 font-body-landing text-base text-ploy-neutral-inverse-950 outline-none transition-colors duration-200 placeholder:text-ploy-neutral-inverse-500 focus:border-ploy-accent-primary-400 focus:ring-2 focus:ring-ploy-accent-primary-100';

export const authLabel =
  'mb-1.5 block font-body-landing text-sm font-semibold text-ploy-neutral-inverse-950';

// Landing hero primary CTA ("Start for free"), plus disabled states the
// landing never needed.
export const authPrimaryButton =
  'inline-flex w-full items-center justify-center rounded-full bg-ploy-accent-primary-500 px-8 py-3.5 font-button text-lg font-semibold text-ploy-text-on-accent-primary shadow-lg shadow-ploy-accent-primary-500/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ploy-accent-primary-600 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-ploy-accent-primary-500';

// Landing secondary CTA ("See a live league"), sized for the header pill.
export const authSecondaryPill =
  'inline-flex items-center justify-center rounded-full border border-ploy-border-primary bg-ploy-background-primary px-5 py-2.5 font-button text-sm font-semibold text-ploy-neutral-inverse-950 transition-all duration-300 hover:border-ploy-accent-primary-300 hover:text-ploy-accent-primary-600';

export const authLink =
  'font-semibold text-ploy-accent-primary-600 underline-offset-2 hover:underline';

export const authErrorBanner =
  'mb-5 rounded-lg border border-red-300 bg-red-50 px-4 py-4 font-body-landing text-sm font-medium text-red-800';

// Marketing-site URL for the header logo (localhost admin root when local).
export function getHomeUrl(): string {
  const { hostname } = window.location;
  const isLocalEnv =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
  return isLocalEnv ? 'http://localhost:3030' : 'https://www.staty.io';
}

type AuthShellProps = {
  /** Right side of the header, e.g. the Sign in ⇄ Sign up swap pill. */
  headerAction?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Rendered under the card (e.g. "Don't have an account?"). */
  footer?: React.ReactNode;
  children: React.ReactNode;
};

export default function AuthShell({ headerAction, title, subtitle, footer, children }: AuthShellProps) {
  return (
    <div className="flex min-h-screen flex-col bg-ploy-background-primary font-body-landing">
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <a href={getHomeUrl()} className="transition-opacity hover:opacity-80">
          <img src={LandingLogo} alt="Staty" className="h-[2.125rem] w-auto sm:h-[2.625rem]" />
        </a>
        {headerAction}
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-4">
        <div className="w-full max-w-[420px]">
          <div className="mb-8 text-center">
            <h1 className="font-heading text-4xl font-bold leading-[1.1] text-ploy-neutral-inverse-950">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 font-body-landing text-lg leading-relaxed text-ploy-neutral-inverse-600">
                {subtitle}
              </p>
            )}
          </div>

          <div className="rounded-2xl border border-ploy-border-primary bg-ploy-background-primary p-6 sm:p-8">
            {children}
          </div>

          {footer && (
            <p className="mt-6 text-center font-body-landing text-sm text-ploy-neutral-inverse-600">
              {footer}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

/** "Already have an account? (Sign in)"-style header action. */
export function AuthHeaderSwap({ prompt, to, label }: { prompt?: string; to: string; label: string }) {
  return (
    <div className="flex items-center gap-3">
      {prompt && (
        <span className="hidden font-body-landing text-sm text-ploy-neutral-inverse-600 sm:inline">
          {prompt}
        </span>
      )}
      <Link to={to} className={authSecondaryPill}>
        {label}
      </Link>
    </div>
  );
}
